import React, { useRef, useState, useEffect } from 'react';
import styled, { css, keyframes } from 'styled-components';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
`;

const Container = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  background: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid ${props => {
    if (props.$isSelected) return '#0099ff';
    if (props.$isOnline) return '#333';
    return '#222';
  }};

  &:hover {
    border-color: ${props => props.$isSelected ? '#0099ff' : '#555'};
    transform: scale(1.02);
  }

  ${props => !props.$isOnline && css`
    opacity: 0.6;
  `}
`;

const Preview = styled.div`
  position: absolute;
  inset: 0;
  background: ${props => props.$backgroundColor || '#0a0a0a'};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px;
`;

const PreviewTitle = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`;

const ScreenCode = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'JetBrains Mono', monospace;
  cursor: pointer;
  z-index: 10;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(0, 153, 255, 0.8);
    transform: scale(1.05);
  }
`;

const WallLabel = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
`;

const StatusIndicator = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$isOnline ? '#00ff88' : '#ff4444'};

  ${props => props.$isOnline && css`
    box-shadow: 0 0 8px #00ff88;
  `}
`;

const ViewLabel = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  background: rgba(0, 0, 0, 0.5);
  padding: 2px 6px;
  border-radius: 3px;
`;

const PairingCode = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 32px;
  font-weight: 700;
  color: #0099ff;
  letter-spacing: 0.1em;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const PairingLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 8px;
`;

const OfflineMessage = styled.div`
  font-size: ${props => props.$compact ? '10px' : '14px'};
  color: rgba(255, 255, 255, 0.4);
  ${props => props.$compact && `
    position: absolute;
    bottom: 4px;
    right: 4px;
  `}
`;

const IframeContainer = styled.div`
  position: absolute;
  inset: 0;
  background: #000;
  overflow: hidden;
`;

const LiveIframe = styled.iframe`
  width: 1920px;
  height: 1080px;
  transform-origin: top left;
  transform: scale(${props => props.$scale || 0.1});
  border: none;
  pointer-events: none;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WALL_NAMES = {
  A: 'North',
  B: 'East',
  C: 'South',
  D: 'West',
};

const VIEW_LABELS = {
  postit: 'PostIt',
  weather: 'Weather',
  'impact-path': 'Impact Path',
  'impact-machine': 'Impact Machine',
  waiting: 'Waiting',
  data: 'Data',
  human: 'Network',
  solutions: 'Solutions',
  futures: 'Futures',
  table: 'Table',
  pivotpoints: 'Pivot Points',
  swot: 'SWOT',
};

/**
 * ScreenThumbnail - Admin grid item representing a single screen
 *
 * @param {string} code - Screen code ('A1', 'B2', etc.)
 * @param {string} wall - Wall identifier ('A', 'B', 'C', 'D')
 * @param {boolean} isOnline - Whether screen is connected
 * @param {string} currentView - Current view ID (e.g., 'postit', 'weather')
 * @param {object} preview - Preview data { backgroundColor, title, stats }
 * @param {string} pairingCode - 6-digit pairing code when awaiting connection
 * @param {function} onClick - Click handler for the code badge (opens view selector)
 * @param {function} onExpand - Click handler for preview area (opens modal)
 * @param {boolean} isSelected - Whether this screen is currently selected
 * @param {boolean} compact - Use compact layout for small thumbnails (iPads)
 * @param {string} livePreviewUrl - URL to show in iframe for live preview mode
 */
const ScreenThumbnail = ({
  code,
  wall,
  isOnline = false,
  currentView = null,
  preview = {},
  pairingCode = null,
  onClick,
  onExpand,
  isSelected = false,
  compact = false,
  livePreviewUrl = null,
}) => {
  const containerRef = useRef(null);
  const [iframeScale, setIframeScale] = useState(0.1);
  const wallName = WALL_NAMES[wall] || wall;
  const viewLabel = VIEW_LABELS[currentView] || currentView;

  // Calculate iframe scale based on container size
  useEffect(() => {
    if (!livePreviewUrl || !containerRef.current) return;

    const updateScale = () => {
      const container = containerRef.current;
      if (container) {
        const containerWidth = container.offsetWidth;
        // Scale 1920px to fit container width
        setIframeScale(containerWidth / 1920);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [livePreviewUrl]);

  const renderContent = () => {
    // Showing pairing code
    if (pairingCode) {
      return (
        <>
          <PairingCode>{pairingCode}</PairingCode>
          <PairingLabel>Enter this code on screen</PairingLabel>
        </>
      );
    }

    // Offline
    if (!isOnline) {
      return <OfflineMessage $compact={compact}>Offline</OfflineMessage>;
    }

    // Online with preview
    return (
      <>
        {preview.title && <PreviewTitle>{preview.title}</PreviewTitle>}
        {preview.stats && (
          <StatsRow>
            {preview.stats.commentCount !== undefined && (
              <StatItem>📝 {preview.stats.commentCount}</StatItem>
            )}
            {preview.stats.participantCount !== undefined && (
              <StatItem>👥 {preview.stats.participantCount}</StatItem>
            )}
            {preview.stats.nodeCount !== undefined && (
              <StatItem>🔗 {preview.stats.nodeCount}</StatItem>
            )}
          </StatsRow>
        )}
      </>
    );
  };

  const handlePreviewClick = (e) => {
    // Only trigger expand if clicking on the preview area, not the code badge
    if (e.target.closest('[data-code-badge]')) return;
    if (onExpand && isOnline) {
      onExpand();
    }
  };

  const handleCodeClick = (e) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <Container
      ref={containerRef}
      $isOnline={isOnline}
      $isSelected={isSelected}
      onClick={handlePreviewClick}
    >
      {livePreviewUrl && isOnline ? (
        <IframeContainer>
          <LiveIframe
            src={livePreviewUrl}
            $scale={iframeScale}
            title={`Screen ${code} preview`}
            loading="lazy"
          />
        </IframeContainer>
      ) : (
        <Preview $backgroundColor={preview.backgroundColor}>
          {renderContent()}
        </Preview>
      )}

      <ScreenCode data-code-badge onClick={handleCodeClick} title="Change view">{code}</ScreenCode>
      {wall && <WallLabel>{wallName}</WallLabel>}

      <StatusIndicator $isOnline={isOnline} />

      {isOnline && currentView && (
        <ViewLabel>{viewLabel}</ViewLabel>
      )}
    </Container>
  );
};

export default ScreenThumbnail;
